import * as React from 'react';

import moment from 'moment';
import type Moment from 'moment';

import {getComparisonDatesFromRange} from 'nutshell-core/get-comparison-dates-from-range';
import {getDateRangeFromDateFilter} from 'nutshell-core/get-date-range-from-date-filter';
import type {ComparisonRangeType, DateRange} from 'nutshell-core/types';

import {Calendar} from '../calendar/calendar';
import {Popover} from '../popover/popover';

import {DateRangePopoverFormSection} from './date-range-popover-form-section';

import styles from './date-range-picker-form.module.css';

const DEFAULT_COMPARISON_RANGE = 'prevYear';

type Props = {
    anchor: HTMLElement;
    filter: string;
    dateRangeOptions: Array<{
        id: string;
        value: string;
    }>;
    comparisonType?: ComparisonRangeType;
    onChange: (arg1: string, comparisonType?: ComparisonRangeType) => any;
    onClose: () => any;
    hasComparison: boolean;
    // @ts-expect-error
    minDate?: Moment;
    // @ts-expect-error
    maxDate?: Moment;
    // Is this date-range-picker being used to mimic a filter capsule?
    // If so, we only show two calendars
    isFilterCapsule?: boolean;
};

type State = {
    // @ts-expect-error
    startDate: Moment;
    // @ts-expect-error
    endDate: Moment;
    comparisonDates: {
        prevPeriod: DateRange;
        prevYear: DateRange;
    };
    // @ts-expect-error
    hoveredStartDate: Moment;
    // @ts-expect-error
    hoveredEndDate: Moment;
    rangeSelection?: 'start' | 'end';
    // @ts-expect-error
    mainCalendarDisplayDate: Moment;
    selectValue: string;
    comparisonType: ComparisonRangeType;
    useComparisonMode: boolean;
    isCurrentlyDisplayingTypedDates: boolean;
};

export class DateRangePickerForm extends React.Component<Props, State> {
    fiscalYearStart: string;
    workWeekStart: string;

    constructor(props: Props) {
        super(props);

        this.fiscalYearStart =
            typeof NutshellFrontendSettings !== 'undefined'
                ? NutshellFrontendSettings.fiscal_year
                : '1-1';

        this.workWeekStart =
            typeof NutshellFrontendSettings !== 'undefined'
                ? NutshellFrontendSettings.work_week_start
                : '7';

        const dateRange = getDateRangeFromDateFilter(
            props.filter,
            moment(),
            this.fiscalYearStart,
            this.workWeekStart
        );

        const comparisonType = props.comparisonType
            ? props.comparisonType
            : DEFAULT_COMPARISON_RANGE;

        const comparisonDates = this.calculateComparisonDates(props.filter);

        const selectedRange = props.dateRangeOptions.find((range) => range.id === props.filter);
        const selectValue = selectedRange ? selectedRange.id : 'custom';

        this.state = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            comparisonDates,
            hoveredStartDate: dateRange.startDate,
            hoveredEndDate: dateRange.endDate,
            rangeSelection: undefined,
            mainCalendarDisplayDate: dateRange.endDate,
            selectValue: selectValue,
            comparisonType: comparisonType,
            useComparisonMode: Boolean(props.comparisonType),
            isCurrentlyDisplayingTypedDates: false,
        };
    }

    componentDidMount() {
        // @ts-expect-error
        window.addEventListener('keydown', this.handleKeydownEvent);
    }

    componentWillUnmount() {
        // @ts-expect-error
        window.removeEventListener('keydown', this.handleKeydownEvent);
    }

    handleKeydownEvent = (e: React.KeyboardEvent<any>) => {
        if (e.which === Nut.keys.RETURN) {
            this.handleSubmit();
        }
    };

    render() {
        const {
            startDate,
            endDate,
            comparisonDates,
            hoveredStartDate,
            hoveredEndDate,
            rangeSelection,
        } = this.state;

        const minDate = rangeSelection === 'end' ? startDate : this.props.minDate;

        const onlyShowTwoCalendars = this.props.isFilterCapsule;

        return (
            <Popover
                className='ui-popover--date-range'
                location='bottom-left'
                isNubDark={true}
                customStyles={onlyShowTwoCalendars ? {marginLeft: '-2px'} : {marginLeft: '-10px'}}
                onBlur={this.props.onClose}
                anchor={this.props.anchor}
            >
                <div className={styles['form-container']}>
                    <Calendar
                        width={220}
                        hasRightControl={false}
                        selectedDates={[startDate, endDate]}
                        comparisonDates={[
                            comparisonDates[this.state.comparisonType].startDate,
                            comparisonDates[this.state.comparisonType].endDate,
                        ]}
                        useComparisonMode={this.state.useComparisonMode}
                        hoveredDates={[hoveredStartDate, hoveredEndDate]}
                        onLeftControlClick={this.handleLeftControlClick}
                        onDateClick={this.handleDateClick}
                        onDateMouseOver={this.handleDateMouseOver}
                        onDateMouseOut={this.handleDateMouseOut}
                        minDate={minDate}
                        maxDate={this.props.maxDate}
                        momentDisplayDate={this.state.mainCalendarDisplayDate
                            .clone()
                            .subtract(1, 'month')}
                    />
                    <Calendar
                        width={220}
                        hasLeftControl={false}
                        hasRightControl={onlyShowTwoCalendars}
                        selectedDates={[startDate, endDate]}
                        comparisonDates={[
                            comparisonDates[this.state.comparisonType].startDate,
                            comparisonDates[this.state.comparisonType].endDate,
                        ]}
                        useComparisonMode={this.state.useComparisonMode}
                        hoveredDates={[hoveredStartDate, hoveredEndDate]}
                        onDateMouseOver={this.handleDateMouseOver}
                        onDateMouseOut={this.handleDateMouseOut}
                        onRightControlClick={this.handleRightControlClick}
                        onDateClick={this.handleDateClick}
                        minDate={minDate}
                        maxDate={this.props.maxDate}
                        momentDisplayDate={this.state.mainCalendarDisplayDate}
                    />
                    {!onlyShowTwoCalendars ? (
                        <Calendar
                            width={220}
                            hasLeftControl={false}
                            selectedDates={[startDate, endDate]}
                            comparisonDates={[
                                comparisonDates[this.state.comparisonType].startDate,
                                comparisonDates[this.state.comparisonType].endDate,
                            ]}
                            useComparisonMode={this.state.useComparisonMode}
                            hoveredDates={[hoveredStartDate, hoveredEndDate]}
                            onDateMouseOver={this.handleDateMouseOver}
                            onDateMouseOut={this.handleDateMouseOut}
                            onRightControlClick={this.handleRightControlClick}
                            minDate={minDate}
                            maxDate={this.props.maxDate}
                            onDateClick={this.handleDateClick}
                            momentDisplayDate={this.state.mainCalendarDisplayDate
                                .clone()
                                .add(1, 'month')}
                        />
                    ) : undefined}
                    <DateRangePopoverFormSection
                        hasComparison={this.props.hasComparison}
                        comparisonType={this.state.comparisonType}
                        useComparisonMode={this.state.useComparisonMode}
                        comparisonDates={comparisonDates}
                        selectValue={this.state.selectValue}
                        selectedDates={[startDate, endDate]}
                        dateRangeOptions={this.props.dateRangeOptions}
                        onPeriodChange={this.handlePeriodChange}
                        onComparisonChange={this.handleComparisonChange}
                        onUseComparisonModeChange={this.handleUseComparisonModeChange}
                        onStartOfRangeFocus={this.handleStartOfRangeFocus}
                        onEndOfRangeFocus={this.handleEndOfRangeFocus}
                        onStartOfRangeChange={this.handleExternalStartOfRangeChange}
                        onEndOfRangeChange={this.handleExternalEndOfRangeChange}
                        isCurrentlyDisplayingTypedDates={this.state.isCurrentlyDisplayingTypedDates}
                        onCancel={this.props.onClose}
                        onSubmit={this.handleSubmit}
                    />
                </div>
            </Popover>
        );
    }

    handlePeriodChange = (newPeriod: string) => {
        if (newPeriod === 'custom') {
            this.setState(() => ({
                selectValue: newPeriod,
                rangeSelection: 'start',
            }));
        } else {
            const {startDate, endDate} = getDateRangeFromDateFilter(
                newPeriod,
                moment(),
                this.fiscalYearStart,
                this.workWeekStart
            );
            this.setState(() => ({
                selectValue: newPeriod,
                startDate: startDate,
                endDate: endDate,
                hoveredStartDate: startDate,
                hoveredEndDate: endDate,

                // One-off UX improvement where last month should just select
                // the last month but not tweak the calendar's displayed months
                mainCalendarDisplayDate: newPeriod === 'lm' ? startDate : endDate,
                rangeSelection: undefined,
            }));
        }
        this.setState(this.setComparisonDates);
    };

    handleComparisonChange = (newComparisonType: ComparisonRangeType) => {
        this.setState(() => ({comparisonType: newComparisonType}));
        this.setState(this.setComparisonDates);
    };

    handleUseComparisonModeChange = (shouldUse: boolean) => {
        this.setState({
            useComparisonMode: shouldUse,
        });
    };

    // @ts-expect-error
    handleDateClick = (date: Moment) => {
        if (this.state.rangeSelection === 'end') {
            this.setState(() => ({
                endDate: date,
                rangeSelection: 'start',
            }));
        } else {
            this.setState(() => ({
                startDate: date,
                rangeSelection: 'end',
            }));
        }
        this.setState(() => ({
            selectValue: 'custom',
            isCurrentlyDisplayingTypedDates: false,
        }));
        this.setState(this.setComparisonDates);
    };

    // @ts-expect-error
    handleExternalStartOfRangeChange = (momentDate: Moment) => {
        this.setState(() => ({
            startDate: momentDate,
            hoveredStartDate: momentDate,
            selectValue: 'custom',
            isCurrentlyDisplayingTypedDates: true,
            mainCalendarDisplayDate: momentDate,
        }));
        this.setState(this.setComparisonDates);
    };

    // @ts-expect-error
    handleExternalEndOfRangeChange = (momentDate: Moment) => {
        this.setState(() => ({
            endDate: momentDate,
            hoveredEndDate: momentDate,
            selectValue: 'custom',
            isCurrentlyDisplayingTypedDates: true,
            mainCalendarDisplayDate: momentDate,
        }));
        this.setState(this.setComparisonDates);
    };

    // @ts-expect-error
    handleDateMouseOver = (date: Moment) => {
        if (this.state.rangeSelection === 'end') {
            this.setState({hoveredEndDate: date});
        } else {
            this.setState({hoveredStartDate: date});
        }
    };

    handleDateMouseOut = () => {
        // @ts-expect-error
        this.setState((prevState) => {
            if (prevState.rangeSelection === 'end') {
                return {hoveredEndDate: prevState.endDate};
            } else {
                return {hoveredStartDate: prevState.startDate};
            }
        });
    };

    handleStartOfRangeFocus = () => {
        this.setState((prevState) => ({
            rangeSelection: 'start',
            mainCalendarDisplayDate: prevState.startDate,
        }));
    };

    handleEndOfRangeFocus = () => {
        this.setState((prevState) => ({
            rangeSelection: 'end',
            mainCalendarDisplayDate: prevState.endDate,
        }));
    };

    handleLeftControlClick = () => {
        this.setState((prevState) => {
            const newDate = prevState.mainCalendarDisplayDate.clone().subtract(1, 'months');

            return {mainCalendarDisplayDate: newDate};
        });
    };

    handleRightControlClick = () => {
        this.setState((prevState) => {
            const newDate = prevState.mainCalendarDisplayDate.clone().add(1, 'months');

            return {mainCalendarDisplayDate: newDate};
        });
    };

    calculateComparisonDates = (filter: string) => {
        const prevYearDates = getComparisonDatesFromRange(
            filter,
            'prevYear',
            this.fiscalYearStart,
            this.workWeekStart
        );
        const prevPeriodDates = getComparisonDatesFromRange(
            filter,
            'prevPeriod',
            this.fiscalYearStart,
            this.workWeekStart
        );

        return {
            prevYear: prevYearDates,
            prevPeriod: prevPeriodDates,
        };
    };

    setComparisonDates = (state: State) => {
        const filter = getFilterStringFromSelectValue(state);

        return {
            comparisonDates: this.calculateComparisonDates(filter),
        };
    };

    handleSubmit = () => {
        const filterString = getFilterStringFromSelectValue(this.state);
        const comparisonType = this.state.useComparisonMode ? this.state.comparisonType : undefined;

        this.props.onChange(filterString, comparisonType);
    };
}

const getFilterStringFromSelectValue = (state: State) => {
    if (state.selectValue !== 'custom') {
        return state.selectValue;
    } else {
        const startDateTimestamp = state.startDate.startOf('day').unix();
        const endDateTimestamp = state.endDate.endOf('day').unix();

        return `${startDateTimestamp} TO ${endDateTimestamp}`;
    }
};
